import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  VStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Icon,
  Center,
  HStack,
} from '@chakra-ui/react';
import { QrCode, Clock, Phone } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import tableOrderingService from '../../services/tableOrderingService';
import { useTableContext } from '../../hooks/useTableContext';
import TableOrderingDashboard from './TableOrderingDashboard';
import LoadingScreen from '../common/LoadingScreen';

/**
 * TableOrderingEntry Component
 * Entry point for QR code table ordering
 * Validates table access and displays ordering interface
 */
const TableOrderingEntry: React.FC = () => {
  const { t } = useTranslation();
  const { context, isLoading: isContextLoading, hasContext } = useTableContext();
  const [validationError, setValidationError] = useState<string | null>(null);


  // Validate table access
  const {
    data: validationData,
    isLoading: isValidating,
    error: validationQueryError
  } = useQuery({
    queryKey: ['validateTableAccess', context],
    queryFn: async () => {
      if (!context) return null;
      const response = await tableOrderingService.validateTableAccess(context);
      return response.data;
    },
    enabled: !!context,
    retry: 1,
  });

  // Get table information
  const {
    data: tableInfo,
    isLoading: isLoadingTable,
    error: tableError
  } = useQuery({
    queryKey: ['tableInfo', context],
    queryFn: async () => {
      if (!context) return null;
      const response = await tableOrderingService.getTableInfo(context);
      return response.data;
    },
    enabled: !!context && validationData?.valid,
    retry: 1,
  });

  // Check restaurant availability
  const {
    data: availabilityData,
    isLoading: isCheckingAvailability,
    error: availabilityError
  } = useQuery({
    queryKey: ['restaurantAvailability', context],
    queryFn: async () => {
      if (!context) return null;
      const response = await tableOrderingService.getRestaurantAvailability(
        context.restaurantId,
        context.apiKey,
        context.code
      );
      return response.data;
    },
    enabled: !!context && validationData?.valid,
    retry: 1,
  });

  // Handle validation failure
  useEffect(() => {
    if (validationQueryError) {
      setValidationError(validationQueryError.message || t('tableOrdering.errors.validateFailed'));
    } else if (validationData && !validationData.valid) {
      setValidationError(validationData.message || t('tableOrdering.errors.validateUnavailable'));
    } else if (tableError) {
      setValidationError(tableError.message || t('tableOrdering.errors.loadTableFailed'));
    } else if (availabilityError) {
      setValidationError(availabilityError.message || t('tableOrdering.errors.checkAvailabilityFailed'));
    } else if (!isContextLoading && !hasContext) {
      // Link without hotel/restaurant/table or a credential (api_key / code)
      setValidationError(t('tableOrdering.errors.invalidQR'));
    } else {
      setValidationError(null);
    }
  }, [validationQueryError, validationData, tableError, availabilityError, isContextLoading, hasContext, t]);

  // Show loading screen while validating
  // No usable context is an error, not a load: spinning here forever is
  // what a QR without a credential used to do.
  if (isContextLoading || (context && (isValidating || isLoadingTable || isCheckingAvailability))) {
    return (
      <LoadingScreen />
    );
  }

  // Show validation error
  if (validationError || !context) {
    return (
      <Container maxW="md" py={12}>
        <VStack spacing={6} align="center">
          <Icon as={QrCode} boxSize={16} color="red.400" />
          
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('tableOrdering.errors.accessTitle')}</AlertTitle>
              <AlertDescription mt={2}>
                {validationError || t('tableOrdering.errors.invalidQR')}
              </AlertDescription>
            </Box>
          </Alert>

          <VStack spacing={3} textAlign="center">
            <Text fontSize="md" color="gray.600">
              {t('tableOrdering.errors.instruction')}
            </Text>

            <Button
              colorScheme="blue"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              {t('tableOrdering.tryAgain')}
            </Button>
          </VStack>
        </VStack>
      </Container>
    );
  }

  // Show restaurant closed message
  if (availabilityData && !availabilityData.is_open) {
    return (
      <Container maxW="md" py={12}>
        <VStack spacing={6} align="center">
          <Icon as={Clock} boxSize={16} color="orange.400" />
          
          <Alert status="warning" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('tableOrdering.closed.title')}</AlertTitle>
              <AlertDescription mt={2}>
                {availabilityData.message}
              </AlertDescription>
            </Box>
          </Alert>

          {tableInfo && (
            <Box 
              p={4} 
              bg="gray.50" 
              borderRadius="lg" 
              border="1px solid" 
              borderColor="gray.200"
              textAlign="center"
            >
              <VStack spacing={2}>
                <Text fontWeight="bold" color="gray.800">
                  {tableInfo.restaurant.name}
                </Text>
                {availabilityData.hours && (
                  <HStack spacing={2}>
                    <Icon as={Clock} boxSize={4} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">
                      {t('tableOrdering.closed.hours', { hours: availabilityData.hours })}
                    </Text>
                  </HStack>
                )}
                {tableInfo.restaurant.phone && (
                  <HStack spacing={2}>
                    <Icon as={Phone} boxSize={4} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">
                      {tableInfo.restaurant.phone}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>
          )}

          {availabilityData.next_opening && (
            <Text fontSize="sm" color="gray.600" textAlign="center">
              {t('tableOrdering.closed.nextOpening', { time: availabilityData.next_opening })}
            </Text>
          )}
        </VStack>
      </Container>
    );
  }

  // Render main ordering interface if everything is valid
  if (tableInfo && availabilityData?.is_open) {
    return (
      <TableOrderingDashboard 
        context={context}
        tableInfo={tableInfo}
      />
    );
  }

  // Fallback loading state
  return (
    <Center h="100vh">
      <VStack spacing={4}>
        <Spinner size="lg" color="blue.500" />
        <Text>{t('tableOrdering.loading')}</Text>
      </VStack>
    </Center>
  );
};

export default TableOrderingEntry;